import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateSkill, useTenants, useUpdateSkill } from "@/lib/queries";
import {
  NEW_SKILL, SKILL_LIMITS, SKILL_SLUG_RE, newSkillPayload, skillCategories, skillChanges,
  skillValues,
} from "@/lib/skills";
import type { RoutingSkill } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Add a skill to the catalog, or edit one (ADR-015).
 *
 * The slug is typed once, on create, and afterwards only shown: lines, numbers, handoffs and reps
 * all name a skill by it, so core-api has no way to rename one and the form does not pretend to.
 * Retiring is not on the form either. It is the list's own action, with a confirmation of its own,
 * because it changes who the ACD rings on every line that requires the skill.
 */

const schema = z.object({
  slug: z
    .string()
    .min(1, "Required")
    .regex(SKILL_SLUG_RE, "Lowercase letters, digits, underscore and hyphen, up to 63"),
  name: z
    .string()
    .trim()
    .min(1, "Required")
    .max(SKILL_LIMITS.name, `At most ${SKILL_LIMITS.name} characters`),
  category: z
    .string()
    .trim()
    .max(SKILL_LIMITS.category, `At most ${SKILL_LIMITS.category} characters`),
  description: z
    .string()
    .trim()
    .max(SKILL_LIMITS.description, `At most ${SKILL_LIMITS.description} characters`),
  is_agent_skill: z.boolean(),
  is_lead_tag: z.boolean(),
  tenant_id: z.number().nullable(),
});
type FormValues = z.infer<typeof schema>;

const BLANK: FormValues = { ...NEW_SKILL, tenant_id: null };

export function SkillFormDialog({
  open, onOpenChange, skill, catalog,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  skill?: RoutingSkill;
  /** The catalog it joins, for the categories already in use. */
  catalog: readonly RoutingSkill[];
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateSkill();
  const update = useUpdateSkill();
  // Only a superadmin names a tenant, and only while there are several: with one, core-api fills it
  // in, and everyone else's is their own whatever they send.
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin && !skill });
  const pickTenant = isSuperadmin && !skill && (tenants?.length ?? 0) > 1;
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: BLANK });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(skill ? { ...skillValues(skill), tenant_id: skill.tenant_id } : BLANK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skill]);

  const values = form.watch();
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: true });

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    if (pickTenant && submitted.tenant_id === null) {
      form.setError("tenant_id", { message: "Choose the tenant whose catalog it joins" });
      return;
    }
    try {
      if (skill) {
        const changes = skillChanges(submitted, skill);
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: skill.id, ...changes });
        toast.success("Skill saved", submitted.name);
      } else {
        await create.mutateAsync({
          ...newSkillPayload(submitted),
          ...(isSuperadmin && submitted.tenant_id !== null ? { tenant_id: submitted.tenant_id } : {}),
        });
        toast.success("Skill added", submitted.name);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the skill.");
    }
  });

  const losingHolders = Boolean(skill?.is_agent_skill) && !values.is_agent_skill && (skill?.holders ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{skill ? "Edit skill" : "New skill"}</DialogTitle>
          <DialogDescription>
            {skill
              ? "What it is called, and what it is for."
              : "Something a line, a number or a handoff can require of the rep it rings: a role, a language, a licence."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="skill-name" required error={errors.name?.message}>
                <Input
                  autoComplete="off"
                  placeholder="Spanish"
                  invalid={Boolean(errors.name)}
                  {...form.register("name")}
                />
              </Field>
              <Field
                label="Slug"
                htmlFor="skill-slug"
                required={!skill}
                error={errors.slug?.message}
                hint={
                  skill
                    ? "Fixed. Lines, numbers, handoffs and reps name the skill by it."
                    : "What lines, numbers, handoffs and reps name it by. It cannot be changed later."
                }
              >
                <Input
                  // Read-only rather than disabled: a disabled field drops out of the submitted values.
                  readOnly={Boolean(skill)}
                  className={cn("font-mono", skill && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="spanish"
                  invalid={Boolean(errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
            </div>

            {pickTenant && (
              <Field
                label="Tenant"
                htmlFor="skill-tenant"
                required
                error={errors.tenant_id?.message}
                hint="Each tenant has a catalog of its own."
              >
                <Select
                  value={values.tenant_id?.toString() ?? ""}
                  onValueChange={(v) => set("tenant_id", Number(v))}
                >
                  <SelectTrigger id="skill-tenant" invalid={Boolean(errors.tenant_id)}>
                    <SelectValue placeholder="Choose a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants ?? []).map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field
              label="Category"
              htmlFor="skill-category"
              error={errors.category?.message}
              hint="How the catalog groups it: role, language, or whatever your lines route on."
            >
              <Input
                autoComplete="off"
                placeholder="language"
                list="skill-categories"
                invalid={Boolean(errors.category)}
                {...form.register("category")}
              />
            </Field>
            <datalist id="skill-categories">
              {skillCategories(catalog).map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>

            <Field
              label="Description"
              htmlFor="skill-description"
              error={errors.description?.message}
              hint="Who should hold it. Shown beside it wherever a rep's skills are set."
            >
              <Textarea
                rows={2}
                className="min-h-16 font-sans text-sm"
                invalid={Boolean(errors.description)}
                value={values.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </Field>

            <SwitchRow
              id="skill-agent"
              title="Rep skill"
              description="Reps hold it at a level from 1 to 5, and a line, a number or a handoff can require it."
              checked={values.is_agent_skill}
              onChange={(checked) => set("is_agent_skill", checked)}
            />
            {losingHolders && (
              <Callout tone="warning">
                {skill?.holders === 1 ? "One rep holds" : `${skill?.holders} reps hold`} it. The ACD
                stops counting it for them, and no line or number can require it. Their levels stay
                for the day it is a rep skill again, unless their skills are saved in between.
              </Callout>
            )}
            <SwitchRow
              id="skill-lead-tag"
              title="Lead tag"
              description="It can describe a lead as well as a rep, so a lead tagged with it can be routed to a rep who holds it, as a campaign's language routing does for Spanish."
              checked={values.is_lead_tag}
              onChange={(checked) => set("is_lead_tag", checked)}
            />
            {!values.is_agent_skill && !values.is_lead_tag && (
              <Callout tone="warning">Neither a rep skill nor a lead tag, it routes nothing.</Callout>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {skill ? "Save changes" : "Add skill"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function SwitchRow({
  id, title, description, checked, onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
      <div>
        <p id={`${id}-label`} className="text-xs font-medium text-text">
          {title}
        </p>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-labelledby={`${id}-label`} />
    </div>
  );
}
